/**
 * ██╗  ██╗ ██████╗ ██████╗  ██████╗
 * ██║ ██╔╝██╔═══██╗██╔══██╗██╔═══██╗
 * █████╔╝ ██║   ██║██████╔╝██║   ██║
 * ██╔═██╗ ██║   ██║██╔══██╗██║   ██║
 * ██║  ██╗╚██████╔╝██████╔╝╚██████╔╝
 * ╚═╝  ╚═╝ ╚═════╝ ╚═════╝  ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import logger from '@services/common';

// Internal dependencies
import { firstToUpperCase } from '@rakuten/common-util/string';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[modules/rx-tolino-epub][TTSManager]';


/**
 * Types of events the Text-to-Speech manager emmits
 * @type {Object}
 */
const EVENT_TYPES = {
  END: 'end',
  PAUSE: 'pause',
  PROGRESS: 'progress',
  RESUME: 'resume',
  START: 'start'
};

/**
 * Supported voice rates
 * @type {Object}
 */
const VOICE_RATES = {
  '0.75': 0.75,
  '1.0':  1,
  '1.25': 1.25,
  '1.5':  1.5,
  '1.75': 1.75,
  '2.0':  2,
  '2.5':  2.5,
  '3.0':  3
};

/**
 * Supported voice pitches
 * @type {Object}
 */
const VOICE_PITCHES = {
  '0.0': 0,
  '0.5': 0.5,
  '1.0': 1,
  '1.5': 1.5,
  '2.0': 2
};

/**
 * The default voice rate
 * @type {Number}
 * @default
 */
const DEFAULT_VOICE_RATE = VOICE_RATES['1.0'];

/**
 * The default voice pitch
 * @type {Number}
 * @default
 */
const DEFAULT_VOICE_PITCH = VOICE_PITCHES['1.0'];


/**
 * Check if the platform the application runs on is capable of all the APIs we
 * need to speak some text. Additionally, we could extend this to disable
 * TTS platforms we don't want TTS for.
 *
 * @return {Boolean} Whether or not the platform is capable of the APIs we need
 */
function isSupportedByPlatform() {
  return (
    ( 'speechSynthesis' in window ) &&
    ( 'SpeechSynthesisUtterance' in window )
  );
}


// -----------------------------------------------------------------------------
// TEXT-TO-SPEECH (TTS) MANAGER
// -----------------------------------------------------------------------------

function TTSManager(SUPPORTED_LANGUAGES) {

  // List of voices available for speaking. Differs based on which voices the
  // operating system provides
  let _voices = [ ];

  // Reference to the currently spoken text. Usually, the WebSpeech API
  // allows for queing multiple instances, but we only deal with one
  // utterance (might change in the future). New instances of
  // SpeechSynthesisUtterance will be created in {@link startSpeaking()}.
  let _utterance = null;

  // The currently handled text. We need to have both the original text
  // set via setText() and the text we are currently uttering, which is a
  // subset of the original text.
  let _currentlyUtteredText = null;

  // The voice to use, defaults to null and has to be set via setVoice()
  let _currentlyUsedVoice = null;

  // Default voice pitch
  let _currentlyUsedVoicePitch = DEFAULT_VOICE_PITCH;

  // Default voice rate
  let _currentlyUsedVoiceRate = DEFAULT_VOICE_RATE;

  // Map of event listeners for the various TTS events. Subscription to an event
  // is done via the on() method (see below). To unsubscribe, call the off()
  // method (see below).
  let _listeners = {
    onProgress: [ ],
    onStart: [ ],
    onStop: [ ],
    onPause: [ ],
    onResume: [ ],
    onEnd: [ ]
  };

  // Indicates whether or not there is something currently beeing spoken.
  // To be aligned with the WebSpeech API, also true when speech is paused
  let _isSpeaking = false;

  // Indicates whether or not the speech is currently paused
  let _isPaused = false;


  // ---------------------------------------------------------------------------
  // HELPER
  // ---------------------------------------------------------------------------

  /**
   * Checks if a text is valid, i.e. if we can use it as a text that can be
   * spoken.
   *
   * @param {String} text The text to validate
   * @return {Boolean} Whether or not the text is valid/usable
   */
  function isTextValid(text) {
    return Boolean(text) && text.constructor === String && text.length > 0;
  }


  /**
   * To make things easier, we handle event names without the "on" prefix. But
   * when we actually wanna listen to these events, we need the prefix. For
   * example, start becomes onStart, end becomes onEnd.
   *
   * @param {string} eventName An event name to transform
   * @returns The transformed event name
   */
  function transformEventName(eventName) {
    return `on${firstToUpperCase(eventName)}`;
  }


  // ---------------------------------------------------------------------------
  // EVENT HELPER
  // ---------------------------------------------------------------------------

  /**
   * Register a callback for an event.
   *
   * @example
   * const tts = new TTSManager();
   * tts.on('start', () => { ... });
   *
   * @param {string} eventName The name of the event to listen for
   * @param {function} callback A function to execute when event is triggered
   */
  function on(eventName, callback) {
    eventName = transformEventName(eventName);
    if (!_listeners[eventName]) {
      logger.warn(`${TAG} [on] Event "${eventName}" is not supported -> Skipping registering event handler`);
      return;
    }

    _listeners[eventName].push(callback);
  }


  /**
   * Unsubscribe from an event.
   *
   * @example
   * const tts = new TTSManager();
   * const callback = () => { ... };
   * tts.on('start', callback);
   * ...
   * tts.off('start', callback);
   *
   * @param {string} eventName The name of the event to remove the callback for
   * @param {function} callback A function to remove
   */
  function off(eventName, callback) {
    eventName = transformEventName(eventName);

    if (!_listeners[eventName]) {
      logger.warn(`${TAG} [off] Event "${eventName}" is not supported -> Skipping removing event handler`);
      return;
    }

    _listeners[eventName] = _listeners[eventName].filter(cb => cb !== callback);
  }


  /**
   * Calls all registered callbacks for the event with the provided data.
   *
   * @param {string} eventName The name of the event
   * @param {*} data Any data that should be send to the event callback
   */
  function emit(eventName, data) {
    eventName = transformEventName(eventName);
    _listeners[eventName].forEach(cb => cb(data));
  }


  // ---------------------------------------------------------------------------
  // EVENT HANDLER
  // ---------------------------------------------------------------------------

  /**
   * Emits a custom event to signal a word boundary has been reached.
   *
   * @param {SpeechSynthesisEvent} e The boundary event from the web API
   */
  function onBoundary(e) {
    emit(EVENT_TYPES.PROGRESS, {
      charIndex: e.charIndex,
      charLength: e.charLength,
      utteredWord: _currentlyUtteredText.substr(e.charIndex, e.charLength)
    });
  }


  /**
   * Emits a custom event to signal speaking has been paused
   *
   * @param {SpeechSynthesisEvent} e The pause event from the web API
   */
  function onPause(e) {
    emit(EVENT_TYPES.PAUSE, {
      charIndex: e.charIndex
    });
  }


  /**
   * Emits a custom event to signal speaking has been resumed
   *
   * @param {SpeechSynthesisEvent} e The resume event from the web API
   */
  function onResume(e) {
    emit(EVENT_TYPES.RESUME, {
      charIndex: e.charIndex
    });
  }


  /**
   * Emits a custom event to signal speaking has been stopped
   *
   * @param {SpeechSynthesisEvent} e The end event from the web API
   */
  function onEnd(e) {
    _isSpeaking = false;
    _isPaused = false;

    emit(EVENT_TYPES.END, {
      initiated: 'automatically',
      charIndex: e.charIndex
    });
  }


  // -----------------------------------------------------------------------------
  // VOICES
  // -----------------------------------------------------------------------------

  /**
   * Returns the list of voices the OS provides for the languages the TTSManager
   * supports.
   *
   * @param {boolean} force Whether or not to force re-retrieval of the voices
   * @returns {Promise<Array>} List of voices for all SUPPORTED_LANGUAGES
   */
  async function getVoices(force = false) {
    if (_voices?.length > 0 && force === false) {
      return _voices;
    }

    function filterVoices(voices) {
      _voices = voices.filter(voice => {
        const lang = voice.lang;
        return SUPPORTED_LANGUAGES[lang];
      });

      return _voices;
    }

    return new Promise(resolve => {
      const availableVoices = window.speechSynthesis.getVoices();

      if (availableVoices.length) {
        // Voices are already loaded into browser
        resolve(filterVoices(availableVoices));
      } else {
        // Wait for browser to load voices (from OS, server etc.)
        const onVoicesChanged = () => {
          const availableVoices = window.speechSynthesis.getVoices();
          resolve(filterVoices(availableVoices));
          window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
        }

        window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
      }
    });
  }


  /**
   * Returns the list of available voices for the provided locale
   *
   * @param {string} locale A locale identifier to filter voices by
   * @returns {Promise<Array>} List of voices for the provided locale
   */
  async function getVoicesForLocale(locale) {
    const voices = await getVoices();

    return voices.filter(voice => {
      let isValid = voice.lang === locale;

      // There is currently a bug in Chrome with the voice "Daniel" that
      // prevents TTS to work correctly. Therefore we filter this voice out
      // for now
      // FIXME: Remove this once the voice works correctly again
      if (voice.name.startsWith('Daniel')) {
        isValid = false;
      }

      return isValid;
    });
  }


  /**
   * Returns the list of available voices for the provided country code
   *
   * @param {string} countryCode A country code to filter voices by
   * @returns {Promise<Array>} List of voices for the provided country code
   */
  async function getVoicesForCountry(countryCode) {
    const voices = await getVoices();

    return voices.filter(voice => {
      const country = voice.lang.split('-')[1];
      return country === countryCode;
    });
  }


  /**
   * Returns the list of avilable voices for the provided language
   *
   * @param {string} language A language identifier to filter voices by
   * @returns {promise<Array>} List of voices for the provided language
   */
  async function getVoicesForLanguage(language) {
    const voices = await getVoices();

    return voices.filter(voice => {
      const lang = voice.lang.split('-')[0];
      return lang === language;
    });
  }


  /**
   * Returns a list of all locales for which voices are available
   *
   * @returns {Promise<Array>} List of locales for which voices are available
   */
  async function getLocalesFromVoices() {
    const voices = await getVoices();

    return [ ...new Set(voices.map(voice => voice.lang)) ];
  }


  /**
   * Sets the voice to use for uttering text
   *
   * @param {SpeechSynthesisVoice} voice The voice to use for uttering any text
   */
  function setVoice(voice) {
    // Make sure we are dealing with a valid voice object
    if (!voice || voice.constructor !== window.SpeechSynthesisVoice) {
      logger.warn(`${TAG} [setVoice] Voice ${voice} is not a valid SpeechSynthesisVoice object.`);
      return;
    }

    _currentlyUsedVoice = voice;
  }


  /**
   * Sets the voice rate (speed) to use for uttering text
   *
   * @param {SpeechSynthesisVoice} rate The voice rate to use for uttering any text
   */
  function setVoiceRate(rate) {
    // Make sure the rate is supported
    if (Object.values(VOICE_RATES).indexOf(rate) < 0) {
      logger.warn(`${TAG} [setVoiceRate] Voice rate "${rate}" is not supported -> Skipping`);
      return;
    }

    _currentlyUsedVoiceRate = rate;
  }


  /**
   * Sets the voice pitch to use for uttering text
   *
   * @param {SpeechSynthesisVoice} pitch The voice pitch to use for uttering any text
   */
  function setVoicePitch(pitch) {
    // Make sure the pitch level is supported
    if (Object.values(VOICE_PITCHES).indexOf(pitch) < 0) {
      logger.warn(`${TAG} [setVoicePitch] Voice pitch "${pitch}" is not supported -> Skipping`);
      return;
    }

    _currentlyUsedVoicePitch = pitch;
  }


  // ---------------------------------------------------------------------------
  // TEXT
  // ---------------------------------------------------------------------------

  /**
   * Sets the text that should be uttered
   *
   * @param {String} text The text that should be uttered
   */
  function setText(text) {
    if (!isTextValid(text)) {
      logger.warn(`${TAG} [setText] Trying to set invalid/empty text.`);
      return;
    }

    _currentlyUtteredText = text;
  }


  // ---------------------------------------------------------------------------
  // SPEECH
  // ---------------------------------------------------------------------------

  /**
   * Start uttering the set text with the set voice and its properties.
   *
   * Before it is possible to actually speak a text, one has to set a text
   * via the {@link setText} method. Additionally, one has to set a voice to
   * be used via the {@link setVoice} method.
   */
  function startSpeaking() {
    // Make sure we have a text that is speakable
    if (isTextValid(_currentlyUtteredText) === false) {
      console.warn(`${TAG} [startSpeaking] Either no text has been set that can be spoken or the text is not valid -> Skipping`);
      return;
    }

    // Make sure a voice has been set to be used to utter the text
    if (!_currentlyUsedVoice) {
      console.warn(`${TAG} [startSpeaking] No voice has been selected to utter this text -> Skipping`);
      return;
    }

    // Create a new utterance object and set it's properties
    _utterance = new window.SpeechSynthesisUtterance();
    _utterance.text = _currentlyUtteredText;
    _utterance.voice = _currentlyUsedVoice;
    _utterance.lang = _currentlyUsedVoice.lang;
    _utterance.rate = _currentlyUsedVoiceRate;
    _utterance.pitch = _currentlyUsedVoicePitch;

    // Register event handlers for TTS events
    _utterance.addEventListener('boundary', onBoundary, false);
    _utterance.addEventListener('pause', onPause, false);
    _utterance.addEventListener('resume', onResume, false);
    _utterance.addEventListener('end', onEnd, false);

    window.speechSynthesis.speak(_utterance);
    emit(EVENT_TYPES.START, {
      text: _currentlyUtteredText
    });

    _isSpeaking = true;
    _isPaused = false;
  }


  /**
   * Pauses the currently uttered text
   */
  function pauseSpeaking() {
    window.speechSynthesis.pause();

    _isPaused = true;
  }


  /**
   * Resumes playback of a previously paused speech.
   */
  function resumeSpeaking() {
    window.speechSynthesis.resume();

    _isPaused = false;
  }


  /**
   * Stops playback completely and resets the manager. The text and voice as
   * well as the voice's properties are preserved, which makes it possible to
   * speak the same text with the same settings again.
   */
  function stopSpeaking(willContinue = false) {
    // NOTE: Canceling will remove all utterances from the queue, but it will
    // not fire an `end` event on the current utterance, we have to do this
    // manually, if needed.
    window.speechSynthesis.cancel();

    if (_utterance) {
      _utterance.removeEventListener('boundary', onBoundary, false);
      _utterance.removeEventListener('pause', onPause, false);
      _utterance.removeEventListener('resume', onResume, false);
      _utterance.removeEventListener('end', onEnd, false);

      _utterance = null;
    }

    _isSpeaking = false;
    _isPaused = false;

    if (willContinue === false) {
      emit(EVENT_TYPES.END, {
        initiated: 'manually'
      });
    }
  }


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    get isSpeaking() { return _isSpeaking },
    get isPaused() { return _isPaused },

    on,
    off,

    getVoices,
    getVoicesForLocale,
    getVoicesForCountry,
    getVoicesForLanguage,
    getLocalesFromVoices,

    setText,
    setVoice,
    setVoiceRate,
    setVoicePitch,

    startSpeaking,
    stopSpeaking,
    pauseSpeaking,
    resumeSpeaking
  };
}


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default TTSManager;
export {
  isSupportedByPlatform,

  EVENT_TYPES,
  VOICE_RATES,
  VOICE_PITCHES
};
